import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    OnInit,
    Output,
    ViewEncapsulation
} from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';

import { BehaviorSubject, combineLatest, Observable, ReplaySubject } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, skip, startWith, switchMap, takeUntil } from 'rxjs/operators';

import { CommonModule } from '@angular/common';
import isEqual from 'lodash-es/isEqual';
import { AUTOCOMPLETE_DEBOUNCE_TIME } from '../../../../core/autocomplete/constants/autocomplete.constants';
import { BaseContainer } from '../../../../core/common/containers/base.abstract.container';
import { BaseNestedFieldDefinition } from '../../../../core/field-definitions/entities/field-definition.entity';
import { ExtendedBaseProfileField } from '../../../../core/profiles/entities/extended-profile-field.entity';
import { ShDynamicDialogService } from '../../../../dynamic-dialog/services/dynamic-dialog.service';
import { ShActionItemComponent } from '../../../../elements/components/action-item/action-item.component';
import {
    ActionItem,
    ActionItemModifier,
    ActionItemStyle
} from '../../../../elements/components/action-item/entities/action-item.entity';
import { IconSize } from '../../../../elements/components/icon/entities/icon-sizes.entity';
import { FormSelectOption } from '../../../../form-controls/entities/form-select-option.entity';
import { MultiFormSelectOptions } from '../../../../form-controls/entities/multi-form-select-options.entity';
import { AdvancedSearchDialogTranslations } from '../../entities/i18n/advanced-search-translations.entity';
import { BaseAdvancedSearchSandbox } from '../../sandbox/advanced-search.sandbox';
import { ShAdvancedSearchRowFieldComponent } from './advanced-search-row-field/advanced-search-row-field.component';
import { ShAdvancedSearchRowIncludeComponent } from './advanced-search-row-include/advanced-search-row-include.component';
import { ShAdvancedSearchRowValueComponent } from './advanced-search-row-value/advanced-search-row-value.component';

@Component({
    standalone: true,
    selector: 'sh-advanced-search-row-container',
    styleUrls: ['./advanced-search-row-container.component.scss'],
    templateUrl: './advanced-search-row-container.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    imports: [
        CommonModule,
        ShAdvancedSearchRowIncludeComponent,
        ShAdvancedSearchRowFieldComponent,
        ShAdvancedSearchRowValueComponent,
        ShActionItemComponent
    ],
    providers: [ShDynamicDialogService]
})
export class ShAdvancedSearchRowContainerComponent extends BaseContainer implements OnInit {
    private readonly addActionItem: ActionItem = {
        label: '',
        icon: 'add',
        iconSize: IconSize.REGULAR,
        style: ActionItemStyle.PureIconButton,
        modifiers: [ActionItemModifier.ActionInInputField],
        eventListener: () => this.addControl()
    };

    private readonly removeActionItem: ActionItem = {
        label: '',
        icon: 'minus',
        iconSize: IconSize.REGULAR,
        style: ActionItemStyle.PureIconButton,
        modifiers: [ActionItemModifier.ActionInInputField],
        eventListener: () => this.removeControl()
    };

    readonly formSelectOptions$: ReplaySubject<MultiFormSelectOptions> = new ReplaySubject<MultiFormSelectOptions>(1);
    readonly valueSearchTerm$: BehaviorSubject<string | undefined> = new BehaviorSubject<string | undefined>(undefined);

    profileFields$: BehaviorSubject<ExtendedBaseProfileField<BaseNestedFieldDefinition>[]> = new BehaviorSubject<
        ExtendedBaseProfileField<BaseNestedFieldDefinition>[]
    >([]);

    arrayCount$: ReplaySubject<number> = new ReplaySubject(1);

    actionItems$!: Observable<ActionItem[]>;
    selectedProfileField$!: Observable<ExtendedBaseProfileField<BaseNestedFieldDefinition>>;
    valueFormSelectOptions$!: Observable<MultiFormSelectOptions>;

    @Input() i18n!: AdvancedSearchDialogTranslations;
    @Input() id!: string;
    @Input() index!: number;
    @Input() label!: string;
    @Input() set profileFields(profileFields: ExtendedBaseProfileField<BaseNestedFieldDefinition>[]) {
        this.profileFields$.next(profileFields);
    }

    @Input() formGroup!: UntypedFormGroup;
    @Input() set formArrayCount(count: number) {
        this.arrayCount$.next(count);
    }

    @Input() set options(options: FormSelectOption[]) {
        this.formSelectOptions$.next({ options, isLiveData: false });
    }

    @Output() shouldAddControl: EventEmitter<number> = new EventEmitter<number>();
    @Output() shouldRemoveControl: EventEmitter<number> = new EventEmitter<number>();

    constructor(private advancedSearchSandbox: BaseAdvancedSearchSandbox<BaseNestedFieldDefinition>) {
        super();
    }

    ngOnInit(): void {
        this.initActionItemsStream();
        this.initProfileFieldStream();
        this.initClearValueOnFieldSelectListener();
        this.initValueFormSelectOptions();
    }

    private initActionItemsStream(): void {
        this.actionItems$ = combineLatest([
            this.formGroup.statusChanges.pipe(startWith(this.formGroup.status)),
            this.arrayCount$
        ]).pipe(
            map(([status, arrayCount]) => this.updateActionItems(arrayCount, status === 'DISABLED')),
            distinctUntilChanged(isEqual),
            takeUntil(this.destroy$)
        );
    }

    private updateActionItems(arrayCount: number, controlDisabled: boolean): ActionItem[] {
        if (arrayCount > 1) {
            const modifiers: ActionItemModifier[] = [];
            if (this.index === 0) {
                modifiers.push(ActionItemModifier.Invisible);
            }

            return [
                { ...this.removeActionItem, disabled: controlDisabled },
                {
                    ...this.addActionItem,
                    disabled: controlDisabled,
                    modifiers
                }
            ] as ActionItem[];
        }

        return [{ ...this.addActionItem, disabled: controlDisabled }] as ActionItem[];
    }

    private initProfileFieldStream(): void {
        this.selectedProfileField$ = this.fieldControl.valueChanges.pipe(
            startWith(this.fieldControl.value),
            filter(
                (profileField): profileField is ExtendedBaseProfileField<BaseNestedFieldDefinition> => !!profileField
            ),
            distinctUntilChanged(isEqual),
            takeUntil(this.destroy$)
        );
    }

    private initClearValueOnFieldSelectListener(): void {
        this.selectedProfileField$
            .pipe(
                skip(1), // Ignore initial value, either it is the first time picking a field or it is an external value
                takeUntil(this.destroy$)
            )
            .subscribe(() => this.valueControl.setValue(null));
    }

    private initValueFormSelectOptions(): void {
        this.valueFormSelectOptions$ = combineLatest([
            this.selectedProfileField$,
            this.valueSearchTerm$.pipe(
                takeUntil(this.destroy$),
                debounceTime(AUTOCOMPLETE_DEBOUNCE_TIME),
                startWith(undefined)
            )
        ]).pipe(
            takeUntil(this.destroy$),
            filter(([profileField, _]) => !!profileField),
            switchMap(([profileField, searchTerm]) =>
                this.advancedSearchSandbox.mapProfileFieldsToValueMultiSelectOptions(
                    profileField,
                    searchTerm,
                    this.valueControl.value
                )
            )
        );
    }

    private addControl(): void {
        this.shouldAddControl.emit(this.index);
    }

    private removeControl(): void {
        this.shouldRemoveControl.emit(this.index);
    }

    public onValueSearchChanged(value: string): void {
        this.valueSearchTerm$.next(value);
    }

    get operandControl(): UntypedFormControl {
        return this.formGroup.get('Operand') as UntypedFormControl;
    }

    get fieldControl(): UntypedFormControl {
        return this.formGroup.get('Field') as UntypedFormControl;
    }

    get valueControl(): UntypedFormControl {
        return this.formGroup.get('Value') as UntypedFormControl;
    }
}
